import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login } from './components/Login';
import { Register } from './components/Register';
import { ForgotPassword } from './components/ForgotPassword';
import { Dashboard } from './components/Dashboard';
import { ExpensesManager } from './components/ExpensesManager';
import { LoanManager } from './components/LoanManager';
import { IncomeManager } from './components/IncomeManager';
import { CreditCardManager } from './components/CreditCardManager';
import { BalanceProjection } from './components/BalanceProjection';
import { Calendar } from './components/Calendar';
import Admin from './components/Admin';
import { DebtSheet } from './components/DebtSheet';
import { Settings } from './components/Settings';
import { useExpenses, useIncomes, useCreditCards, useLoans, usePayoffPlans, useUserBalance } from './hooks/useApiData';
import { payoffPlansApi, scheduleExceptionsApi } from './services/api';
import { Expense, Income, CreditCard, Loan, ScheduleException } from './types';
import { balanceOnDate } from './utils/calculations';
import { expenseSeriesPatch, incomeSeriesPatch, loanSeriesPatch } from './utils/finance/moveOccurrence';
import { PaymentConfirmPrompt } from './components/payoff/PaymentConfirmPrompt';
import { Page, cn } from './components/ui';

type TabId =
  | 'dashboard' | 'expenses' | 'income' | 'cards' | 'loans'
  | 'projection' | 'debtsheet' | 'calendar' | 'settings' | 'admin';

/** Nav model. Most entries are a single destination; Debt is a group that
 *  opens a menu, so the three debt views stay together without three separate
 *  top-level tabs. Admin is filtered by email at render time. */
type NavItem =
  | { kind: 'tab'; tab: TabId; label: string }
  | { kind: 'group'; id: string; label: string; items: { tab: TabId; label: string }[] };

const NAV_ITEMS: NavItem[] = [
  { kind: 'tab', tab: 'dashboard', label: 'Dashboard' },
  { kind: 'tab', tab: 'income', label: 'Income' },
  { kind: 'tab', tab: 'expenses', label: 'Expenses' },
  {
    kind: 'group',
    id: 'debt',
    label: 'Debt',
    items: [
      { tab: 'cards', label: 'Credit Cards' },
      { tab: 'loans', label: 'Loans' },
      { tab: 'debtsheet', label: 'Debt Sheet' },
    ],
  },
  { kind: 'tab', tab: 'calendar', label: 'Calendar' },
  { kind: 'tab', tab: 'projection', label: 'Projections' },
  { kind: 'tab', tab: 'admin', label: 'Admin' },
];

/** Nav sits on the surface, not on a rule. The active tab is a sunk chip with
 *  a 2px accent underline — accent because a tab is a thing you click, which
 *  is the only meaning accent carries in this system. */
const NAV_TAB_CLASS =
  'text-[13px] font-medium px-3 py-[7px] rounded transition-colors duration-[120ms]';


// Main App component with authentication
function AppContent() {
  const { user, isLoading, isAuthenticated, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [authView, setAuthView] = useState<'login' | 'register' | 'forgot'>('login');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // The fixed header's height changes as the nav row wraps — 110px at desktop,
  // 151px at tablet, 185px on a phone. A hardcoded pt-[128px] on <main> hid
  // 23px of content at 834px and 57px at 390px. Measure it and publish it as a
  // custom property so every consumer (main's padding, FormScreen's height)
  // reads the real number.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const apply = () => {
      document.documentElement.style.setProperty(
        '--app-header-h',
        `${Math.round(el.getBoundingClientRect().height)}px`
      );
    };
    apply();
    window.addEventListener('resize', apply);
    // ResizeObserver catches the nav re-wrapping without a viewport resize
    // (a longer email in the user badge, say). jsdom does not implement it.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    return () => {
      window.removeEventListener('resize', apply);
      ro?.disconnect();
    };
  }, []);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const navGroupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openGroup) return;
    const onClick = (e: MouseEvent) => {
      if (navGroupRef.current && !navGroupRef.current.contains(e.target as Node)) {
        setOpenGroup(null);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenGroup(null);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [openGroup]);

  useEffect(() => {
    if (!showUserMenu) return;
    const onClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowUserMenu(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [showUserMenu]);

  // API-based data hooks
  const { data: expenses, create: createExpense, update: updateExpense, delete: deleteExpense, isLoading: expensesLoading } = useExpenses();
  const { data: incomes, create: createIncome, update: updateIncome, delete: deleteIncome, isLoading: incomesLoading } = useIncomes();
  const { data: creditCards, create: createCreditCard, update: updateCreditCard, delete: deleteCreditCard, isLoading: cardsLoading } = useCreditCards();
  const { data: loans, create: createLoan, update: updateLoan, delete: deleteLoan, isLoading: loansLoading } = useLoans();
  const [scheduleExceptions, setScheduleExceptions] = useState<ScheduleException[]>([]);
  const refetchExceptions = useCallback(async () => {
    try {
      setScheduleExceptions(await scheduleExceptionsApi.getAll());
    } catch {
      // A failed load just means nothing is drawn as moved; the rules still
      // render. Better than blanking the calendar.
    }
  }, []);
  useEffect(() => { void refetchExceptions(); }, [refetchExceptions]);
  const { data: payoffPlans, create: createPayoffPlan, update: updatePayoffPlan, delete: deletePayoffPlan, refetch: refetchPlans } = usePayoffPlans();
  const { balance: savedBalance, balanceAsOf, lowBalanceThreshold, updateBalance: setCurrentBalance } = useUserBalance();

  // Label for the balance input that includes the last-updated date inline.
  const balanceLabel = useMemo(() => {
    if (!balanceAsOf) return 'Current Balance:';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const asOf = new Date(balanceAsOf);
    asOf.setHours(0, 0, 0, 0);
    if (asOf.getTime() === today.getTime()) return 'Balance Updated Today:';
    return `Balance Updated on ${asOf.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}:`;
  }, [balanceAsOf]);

  // Adjusted "today's balance" — replays scheduled transactions from balanceAsOf
  // forward to today so consumers always see a current balance even if the user
  // hasn't logged in for several days.
  const currentBalance = useMemo(() => {
    if (!balanceAsOf) return savedBalance;
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    const asOf = new Date(balanceAsOf);
    asOf.setHours(0, 0, 0, 0);
    if (asOf >= t) return savedBalance;
    return balanceOnDate({
      openingBalance: savedBalance,
      openingDate: asOf,
      targetDate: t,
      expenses,
      incomes,
      loans,
      creditCards,
      plans: payoffPlans,
    });
  }, [savedBalance, balanceAsOf, expenses, incomes, payoffPlans, creditCards, loans]);



  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <div className="text-center">
          <p className="text-[13px] text-ink-muted">
            Loading SmartySpend
          </p>
        </div>
      </div>
    );
  }

  // Show auth forms if not authenticated
  if (!isAuthenticated) {
    if (authView === 'login') {
      return (
        <Login
          onToggleMode={() => setAuthView('register')}
          onForgotPassword={() => setAuthView('forgot')}
        />
      );
    } else if (authView === 'register') {
      return <Register onToggleMode={() => setAuthView('login')} />;
    } else {
      return <ForgotPassword onBack={() => setAuthView('login')} />;
    }
  }

  // Wrapper functions for API calls with proper error handling
  const addExpense = async (expense: Omit<Expense, 'id'>) => {
    try {
      await createExpense(expense);
    } catch (error) {
      console.error('Failed to add expense:', error);
      // TODO: Show error toast
    }
  };

  const onUpdateExpense = async (id: string, updates: Partial<Expense>) => {
    try {
      await updateExpense(id, updates);
    } catch (error) {
      console.error('Failed to update expense:', error);
      // TODO: Show error toast
    }
  };

  const onDeleteExpense = async (id: string) => {
    try {
      await deleteExpense(id);
    } catch (error) {
      console.error('Failed to delete expense:', error);
      // TODO: Show error toast
    }
  };

  const addIncome = async (income: Omit<Income, 'id'>) => {
    try {
      await createIncome(income);
    } catch (error) {
      console.error('Failed to add income:', error);
      // TODO: Show error toast
    }
  };

  const onUpdateIncome = async (id: string, updates: Partial<Income>) => {
    try {
      await updateIncome(id, updates);
    } catch (error) {
      console.error('Failed to update income:', error);
      // TODO: Show error toast
    }
  };

  const onDeleteIncome = async (id: string) => {
    try {
      await deleteIncome(id);
    } catch (error) {
      console.error('Failed to delete income:', error);
      // TODO: Show error toast
    }
  };

  const addCreditCard = async (card: Omit<CreditCard, 'id'>) => {
    try {
      await createCreditCard(card);
    } catch (error) {
      console.error('Failed to add credit card:', error);
      // TODO: Show error toast
    }
  };

  const onUpdateCreditCard = async (id: string, updates: Partial<CreditCard>) => {
    try {
      await updateCreditCard(id, updates);
    } catch (error) {
      console.error('Failed to update credit card:', error);
      // TODO: Show error toast
    }
  };

  const onDeleteCreditCard = async (id: string) => {
    try {
      await deleteCreditCard(id);
      // Note: Backend handles deleting related payoff scenarios via cascade
    } catch (error) {
      console.error('Failed to delete credit card:', error);
      // TODO: Show error toast
    }
  };

  const addLoan = async (loan: Omit<Loan, 'id'>) => {
    try {
      await createLoan(loan);
    } catch (error) {
      console.error('Failed to add loan:', error);
      // TODO: Show error toast
    }
  };

  const onUpdateLoan = async (id: string, updates: Partial<Loan>) => {
    try {
      await updateLoan(id, updates);
    } catch (error) {
      console.error('Failed to update loan:', error);
      // TODO: Show error toast
    }
  };

  const onDeleteLoan = async (id: string) => {
    try {
      await deleteLoan(id);
    } catch (error) {
      console.error('Failed to delete loan:', error);
      // TODO: Show error toast
    }
  };



  return (
    <div className="min-h-screen bg-ground">
      <div ref={headerRef} className="fixed top-0 left-0 right-0 z-50 bg-surface border-b border-rule-base">
        <header className="border-b border-rule">
          <div className="mx-auto w-full max-w-[1240px] px-5 sm:px-[44px] py-4">
            <div className="flex items-center justify-between gap-4">
              <span className="text-[19px] font-semibold tracking-[-0.02em] leading-none text-ink">
                SmartySpend
              </span>

              <div className="relative shrink-0" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(o => !o)}
                  aria-expanded={showUserMenu}
                  className="text-[12.5px] font-medium text-ink-soft hover:text-ink transition-colors duration-[120ms] flex items-center gap-2"
                >
                  <span className="truncate max-w-[120px] sm:max-w-[200px]" title={user?.email}>
                    {user?.name || user?.email}
                  </span>
                  <span aria-hidden="true">{showUserMenu ? '\u2013' : '+'}</span>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 z-30 min-w-[220px] bg-surface border border-rule-base rounded-xl shadow-lift overflow-hidden">
                    <div className="px-4 py-3 border-b border-rule">
                      <div className="text-[11px] font-semibold text-ink-muted">
                        Signed in as
                      </div>
                      <div className="text-[13px] text-ink truncate mt-1" title={user?.email}>
                        {user?.email}
                      </div>
                    </div>
                    <button
                      onClick={() => { setShowUserMenu(false); setActiveTab('settings'); }}
                      className="block w-full text-left px-4 py-2.5 text-[12.5px] font-medium text-ink-soft hover:bg-ground hover:text-ink transition-colors duration-[120ms]"
                    >
                      Settings
                    </button>
                    <button
                      onClick={() => { setShowUserMenu(false); logout(); }}
                      className="block w-full text-left px-4 py-2.5 text-[12.5px] font-medium text-bad border-t border-rule hover:bg-bad-tint transition-colors duration-[120ms]"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <nav>
          <div className="mx-auto w-full max-w-[1240px] px-5 sm:px-[44px]">
            <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 py-2">
              {/* One wrapping tab row at every width — no sidebar, no
                  drawer. Debt is a group: its three destinations open in a
                  menu rather than taking three top-level slots. */}
              <div className="flex flex-wrap items-center gap-x-1 gap-y-1" ref={navGroupRef}>
                {NAV_ITEMS.map(item => {
                  if (item.kind === 'tab') {
                    if (item.tab === 'admin' && user?.email !== 'joel.lytle@gmail.com') return null;
                    return (
                      <button
                        key={item.tab}
                        onClick={() => {
                          setOpenGroup(null);
                          setActiveTab(item.tab);
                        }}
                        className={cn(
                          NAV_TAB_CLASS,
                          activeTab === item.tab
                            ? 'text-ink bg-ground-sunk shadow-nav-active'
                            : 'text-ink-muted hover:text-ink'
                        )}
                      >
                        {item.label}
                      </button>
                    );
                  }

                  // A group reads as active whenever any of its destinations is.
                  const groupActive = item.items.some(sub => sub.tab === activeTab);
                  const isOpen = openGroup === item.id;
                  return (
                    <div key={item.id} className="relative">
                      <button
                        onClick={() => setOpenGroup(isOpen ? null : item.id)}
                        aria-expanded={isOpen}
                        aria-haspopup="menu"
                        className={cn(
                          NAV_TAB_CLASS,
                          'inline-flex items-center gap-2',
                          groupActive
                            ? 'text-ink bg-ground-sunk shadow-nav-active'
                            : 'text-ink-muted hover:text-ink'
                        )}
                      >
                        {item.label}
                        <span aria-hidden="true" className="text-[8px] leading-none">
                          {isOpen ? '\u25B2' : '\u25BC'}
                        </span>
                      </button>
                      {isOpen && (
                        <div
                          role="menu"
                          className="absolute left-0 top-full mt-1 z-30 min-w-[180px] bg-surface border border-rule-base rounded-xl shadow-lift overflow-hidden"
                        >
                          {item.items.map((sub, i) => (
                            <button
                              key={sub.tab}
                              role="menuitem"
                              onClick={() => {
                                setOpenGroup(null);
                                setActiveTab(sub.tab);
                              }}
                              className={cn(
                                'block w-full text-left px-4 py-2.5 text-[12.5px] font-medium transition-colors duration-[120ms]',
                                i > 0 && 'border-t border-rule',
                                // Selection is the bone fill; red stays reserved.
                                activeTab === sub.tab
                                  ? 'bg-accent-tint text-accent'
                                  : 'text-ink-soft hover:bg-ground hover:text-ink'
                              )}
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-baseline gap-3 shrink-0">
                <label
                  htmlFor="current-balance"
                  className="text-[11px] font-semibold text-ink-muted whitespace-nowrap"
                >
                  {balanceLabel}
                </label>
                <input
                  id="current-balance"
                  type="number"
                  step="0.01"
                  value={savedBalance}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCurrentBalance(value === '' ? 0 : parseFloat(value) || 0);
                  }}
                  className="w-24 text-right bg-surface text-ink text-[14px] font-semibold px-2.5 py-1.5 border border-rule-strong rounded-lg focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-accent/15 transition-colors duration-[120ms]"
                />
              </div>
            </div>
          </div>
        </nav>
      </div>

      <main className="pt-[var(--app-header-h)]">
        <Page>
        {activeTab === 'dashboard' && (
          <Dashboard
            currentBalance={currentBalance}
            expenses={expenses}
            incomes={incomes}
            creditCards={creditCards}
            plans={payoffPlans}
            loans={loans}
            lowBalanceThreshold={lowBalanceThreshold}
          />
        )}
        
        {activeTab === 'expenses' && (
          <ExpensesManager
            expenses={expenses}
            onAddExpense={addExpense}
            onUpdateExpense={onUpdateExpense}
            onDeleteExpense={onDeleteExpense}
          />
        )}
        
        {activeTab === 'income' && (
          <IncomeManager
            incomes={incomes}
            onAddIncome={addIncome}
            onUpdateIncome={onUpdateIncome}
            onDeleteIncome={onDeleteIncome}
          />
        )}
        
        {activeTab === 'cards' && (
          <CreditCardManager
            creditCards={creditCards}
            plans={payoffPlans}
            onAddCard={addCreditCard}
            onUpdateCard={onUpdateCreditCard}
            onDeleteCard={onDeleteCreditCard}
            onCreatePlan={createPayoffPlan}
            onUpdatePlan={updatePayoffPlan}
            onCancelPlan={deletePayoffPlan}
            currentBalance={currentBalance}
            balanceAsOf={balanceAsOf}
            lowBalanceThreshold={lowBalanceThreshold}
            incomes={incomes}
            expenses={expenses}
            loans={loans}
          />
        )}
        
        {activeTab === 'projection' && (
          <BalanceProjection
            currentBalance={currentBalance}
            expenses={expenses}
            incomes={incomes}
            plans={payoffPlans}
            creditCards={creditCards}
            loans={loans}
            lowBalanceThreshold={lowBalanceThreshold}
          />
        )}
        
        {activeTab === 'loans' && (
          <LoanManager
            loans={loans}
            onAddLoan={addLoan}
            onUpdateLoan={onUpdateLoan}
            onDeleteLoan={onDeleteLoan}
          />
        )}

        {activeTab === 'debtsheet' && (
          <DebtSheet
            creditCards={creditCards}
            loans={loans}
            plans={payoffPlans}
          />
        )}

        {activeTab === 'calendar' && (
          <Calendar
            onMovePayment={async (paymentId, date) => {
              await payoffPlansApi.movePayment(paymentId, date);
              await refetchPlans();
            }}
            onUnconfirmPayment={async paymentId => {
              await payoffPlansApi.confirmPayment(paymentId, 'PLANNED');
              await refetchPlans();
            }}
            scheduleExceptions={scheduleExceptions}
            onMoveOccurrence={async ({ kind, sourceId, originalDate, newDate, scope }) => {
              // "Just this one" records an exception and leaves the rule alone.
              if (scope === 'one') {
                await scheduleExceptionsApi.move(kind, sourceId, originalDate, newDate);
                await refetchExceptions();
                return;
              }
              // "Every one" rewrites the rule. Each frequency keeps its day in
              // a different field, so the patch is worked out in one place.
              if (kind === 'expense') {
                const item = expenses.find(x => x.id === sourceId);
                if (item) await updateExpense(sourceId, expenseSeriesPatch(item, newDate));
              } else if (kind === 'income') {
                const item = incomes.find(x => x.id === sourceId);
                if (item) await updateIncome(sourceId, incomeSeriesPatch(item, originalDate, newDate));
              } else {
                const item = loans.find(x => x.id === sourceId);
                if (item) await updateLoan(sourceId, loanSeriesPatch(item, newDate));
              }
            }}
            currentBalance={savedBalance}
            balanceAsOf={balanceAsOf}
            expenses={expenses}
            incomes={incomes}
            plans={payoffPlans}
            creditCards={creditCards}
            loans={loans}
            lowBalanceThreshold={lowBalanceThreshold}
          />
        )}

        {activeTab === 'settings' && (
          <Settings />
        )}

        {activeTab === 'admin' && user?.email === 'joel.lytle@gmail.com' && (
          <Admin />
        )}
        </Page>
      </main>

      <PaymentConfirmPrompt refreshKey={payoffPlans.length} onResolved={refetchPlans} />

    </div>
  );
}

// Wrapper to add key based on user ID to force remount on user change
function AppWithAuth() {
  const { user } = useAuth();
  // Use user ID as key to force component remount when user changes
  // This clears all cached state when logging out/switching users
  return <AppContent key={user?.id || 'logged-out'} />;
}

function App() {
  return (
    <AuthProvider>
      <AppWithAuth />
    </AuthProvider>
  );
}

export default App;
